import React, { useEffect, useState, useRef } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Autocomplete,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Swal from "sweetalert2";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import {
    collection,
    query,
    where,
    onSnapshot,
    orderBy,
    getDoc,
    doc,
    Timestamp,
} from "firebase/firestore";
import { auth, db } from "../firebase";

export default function CustomerwiseReport() {
    const printRef = useRef();

    const [propertyId, setPropertyId] = useState("");
    const [customers, setCustomers] = useState([]);
    const [customer, setCustomer] = useState(null);
    const [custid, setCustid] = useState("");

    const [fromdate, setFromDate] = useState("");
    const [todate, setToDate] = useState("");

    const [reportData, setReportData] = useState([]);
    const [items, setItems] = useState([]);

    const [total, setTotal] = useState(0);
    const [paid, setPaid] = useState(0);
    const [discount, setDiscount] = useState(0);
    const [remaining, setRemaining] = useState(0);

    const [open, setOpen] = useState(false);

    /* ---------------- FETCH PROPERTY ID ---------------- */
    useEffect(() => {
        const unsub = auth.onAuthStateChanged(async (user) => {
            if (!user) return;

            const snap = await getDoc(doc(db, "authroles", user.uid));
            if (snap.exists()) {
                setPropertyId(snap.data().propertyId);
            }
        });
        return unsub;
    }, []);

    /* ---------------- DEFAULT DATE ---------------- */
    useEffect(() => {
        const today = new Date().toISOString().split("T")[0];
        setFromDate(today);
        setToDate(today);
    }, []);

    /* ---------------- FETCH CUSTOMERS ---------------- */
    useEffect(() => {
        if (!propertyId) return;

        return onSnapshot(
            query(collection(db, "restocustomers"), where("propertyId", "==", propertyId)),
            (snap) =>
                setCustomers(
                    snap.docs.map((d) => ({
                        label: d.data().custname || d.data().name,
                        id: d.id,
                    }))
                )
        );
    }, [propertyId]);

    /* ---------------- GET REPORT ---------------- */
    const getReport = () => {
        if (!custid) {
            Swal.fire("Error", "Select customer", "error");
            return;
        }

        const fromTS = Timestamp.fromDate(
            new Date(fromdate + " 00:00:00")
        );

        const toTS = Timestamp.fromDate(
            new Date(todate + " 23:59:59")
        );


        return onSnapshot(
            query(
                collection(db, "orders"),
                where("propertyId", "==", propertyId),
                where("customerid", "==", custid),
                where("billdate", ">=", fromTS),
                where("billdate", "<=", toTS),
                orderBy("billdate", "desc")
            ),
            async (snap) => {
                let t = 0,
                    p = 0,
                    d = 0,
                    r = 0;

                const rows = [];

                for (const doc1 of snap.docs) {
                    const data = doc1.data();

                    t += Number(data.totalamount || 0);
                    p += Number(data.paid || 0);
                    d += Number(data.discount || 0);
                    r += Number(data.remaining || 0);

                    rows.push({ id: doc1.id, data });
                }

                setTotal(t);
                setPaid(p);
                setDiscount(d);
                setRemaining(r);
                setReportData(rows);
            }
        );
    };

    /* ---------------- VIEW ITEMS ---------------- */
    const handleView = (orderId) => {
        onSnapshot(
            query(collection(db, "orderitems"), where("orderid", "==", orderId)),
            (snap) => {
                setItems(snap.docs.map((d) => d.data()));
                setOpen(true);
            }
        );
    };

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
    });

    return (
        <Box className=" p-3">
            <Card>
                <CardContent>
                    <Typography variant="h5" mb={2}>
                        Customer Wise Report
                    </Typography>
                    <Grid className="row" spacing={2}>
                        <Grid className="col-sm-3">
                            <Autocomplete
                                options={customers}
                                value={customer}
                                size="small"
                                onChange={(e, v) => {
                                    setCustomer(v);
                                    setCustid(v?.id || "");
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select Customer" />
                                )}
                            />
                        </Grid>

                        <Grid className="col-sm-3">
                            <TextField
                                type="date"
                                label="From Date"
                                size="small"

                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                value={fromdate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </Grid>

                        <Grid className="col-sm-3">
                            <TextField
                                type="date"
                                label="To Date"
                                size="small"

                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                value={todate}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </Grid>

                        <Grid className="col-sm-3" alignSelf="end">
                            <Button variant="contained" onClick={getReport}>
                                Get Report
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* REPORT TABLE */}
            <Card sx={{ mt: 2 }}>
                <CardContent ref={printRef}>
                    <Typography align="center" variant="h6">
                        Customer Wise Report
                    </Typography>

                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Total</TableCell>
                                <TableCell>Paid</TableCell>
                                <TableCell>Discount</TableCell>
                                <TableCell>Remaining</TableCell>
                                {/* <TableCell>Action</TableCell> */}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {reportData.map((r, i) => (
                                <TableRow key={r.id}>
                                    <TableCell>{i + 1}</TableCell>
                                    <TableCell>
                                        {moment(r.data.orderdate).format("DD-MM-YYYY")}
                                    </TableCell>
                                    <TableCell>{r.data.totalamount}</TableCell>
                                    <TableCell>{r.data.paid}</TableCell>
                                    <TableCell>{r.data.discount}</TableCell>
                                    <TableCell>{r.data.remaining}</TableCell>
                                    {/* <TableCell>
                                        <IconButton onClick={() => handleView(r.id)}>
                                            <VisibilityIcon />
                                        </IconButton>
                                    </TableCell> */}
                                </TableRow>
                            ))}

                            <TableRow>
                                <TableCell colSpan={2}><b>Total</b></TableCell>
                                <TableCell><b>{total}</b></TableCell>
                                <TableCell><b>{paid}</b></TableCell>
                                <TableCell><b>{discount}</b></TableCell>
                                <TableCell><b>{remaining}</b></TableCell>
                                <TableCell />
                            </TableRow>
                        </TableBody>
                    </Table>
                </CardContent>

                <Box textAlign="center" pb={2}>
                    <Button onClick={handlePrint} variant="outlined">
                        Print
                    </Button>
                </Box>
            </Card>

            {/* ITEMS DIALOG */}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Order Items</DialogTitle>
                <DialogContent>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Item</TableCell>
                                <TableCell>Rate</TableCell>
                                <TableCell>Qty</TableCell>
                                <TableCell>Total</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((i, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>{i.itemname}</TableCell>
                                    <TableCell>{i.rate}</TableCell>
                                    <TableCell>{i.quantity}</TableCell>
                                    <TableCell>{i.total}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
